import { IMemberDetailsData } from 'fitbeat.gym-core';
import {
  ILeaderboardConfig,
  ILeaderboardDataViewModel,
  IMonthlyPulseBadgeItem,
  IMonthlyPulseLeaderboardPanel,
  IMonthlyPulseMediaItem,
} from 'fitbeat.models';

export type MonthlyPulseUnit = 'K' | 'M' | 'B' | '%' | '';

const DECIMALS = 1;

function scaleAndRound(rawValue: number, divisor: number): number {
  const scaled = rawValue / divisor;
  const factor = 10 ** DECIMALS;
  return Math.round(scaled * factor) / factor;
}

export function toPulseValue(rawValue: number, isPercent?: boolean): { value: number; suffix: MonthlyPulseUnit } {
  if (isPercent) { return { value: rawValue, suffix: '%' }; }
  if (rawValue >= 1_000_000_000) { return { value: scaleAndRound(rawValue, 1e9), suffix: 'B' }; }
  if (rawValue >= 1_000_000) { return { value: scaleAndRound(rawValue, 1e6), suffix: 'M' }; }
  if (rawValue >= 1_000) { return { value: scaleAndRound(rawValue, 1e3), suffix: 'K' }; }
  return { value: rawValue, suffix: '' };
}

export interface IMonthlyPulseMeta {
  heading: string;
  selectedMonth: string;
  companyName: string;
  companyLogoUrl: string | null;
  fitillionLogoUrl: string;
  generatedAt: string;
}

export interface IMonthlyPulseCounterMetric {
  toDate: number;
  toDateSuffix: MonthlyPulseUnit;
  thisMonth: number;
  thisMonthSuffix: MonthlyPulseUnit;
}

export interface ISocialSection {
  congratulationsSent: IMonthlyPulseCounterMetric;
  challengesCompleted: IMonthlyPulseCounterMetric;
  interactionsCount: IMonthlyPulseCounterMetric;
  activityGroupMedia: IMonthlyPulseMediaItem;
  interestGroupMedia: IMonthlyPulseMediaItem;
}

export interface IWellnessSection {
  readsThisYear: number;
  readsThisYearSuffix: MonthlyPulseUnit;
  readsThisMonth: number;
  readsThisMonthSuffix: MonthlyPulseUnit;
  listensThisYear: number;
  listensThisYearSuffix: MonthlyPulseUnit;
  listensThisMonth: number;
  listensThisMonthSuffix: MonthlyPulseUnit;
  mediaSpotlight: IMonthlyPulseMediaItem[];
}

export interface IAchievementsSection {
  experiencePointsToDate: number;
  experiencePointsToDateSuffix: MonthlyPulseUnit;
  experiencePointsThisMonth: number;
  experiencePointsThisMonthSuffix: MonthlyPulseUnit;
  achievementsToDate: number;
  achievementsToDateSuffix: MonthlyPulseUnit;
  achievementsThisMonth: number;
  achievementsThisMonthSuffix: MonthlyPulseUnit;
  featuredChallengeJoinPercent: number;
  featuredChallengeJoinPercentSuffix: MonthlyPulseUnit;
  /** True when the backend provided a featured join count (tile hidden otherwise). */
  featuredChallengeJoinVisible: boolean;
  weeklyActivityGoalsPercent: number;
  weeklyActivityGoalsPercentSuffix: MonthlyPulseUnit;
  weeklyActivityGoalsTotal: number;
  weeklyActivityGoalsTotalSuffix: MonthlyPulseUnit;
  dailyStepsGoalsPercent: number;
  dailyStepsGoalsPercentSuffix: MonthlyPulseUnit;
  dailyStepsGoalsTotal: number;
  dailyStepsGoalsTotalSuffix: MonthlyPulseUnit;
  badges: IMonthlyPulseBadgeItem[];
}

export interface ILeaderboardsSection {
  featured: IMonthlyPulseLeaderboardPanel;
  activityGroups: IMonthlyPulseLeaderboardPanel;
}

export interface IWorkoutsByType {
  strengthThisMonth: number;
  strengthThisMonthSuffix: MonthlyPulseUnit;
  cardioThisMonth: number;
  cardioThisMonthSuffix: MonthlyPulseUnit;
  mobilityThisMonth: number;
  mobilityThisMonthSuffix: MonthlyPulseUnit;
  /** Segment percentages from raw monthly counts (not K/M-scaled display values). */
  donutStrengthPct: number;
  donutCardioPct: number;
  donutMobilityPct: number;
}

export interface IExerciseSection {
  stepsThisYear: number;
  stepsThisYearSuffix: MonthlyPulseUnit;
  stepsThisMonth: number;
  stepsThisMonthSuffix: MonthlyPulseUnit;
  aroundTheEarthPercent: number;
  aroundTheEarthPercentSuffix: MonthlyPulseUnit;
  activeMinutesToDate: number;
  activeMinutesToDateSuffix: MonthlyPulseUnit;
  activeMinutesThisMonth: number;
  activeMinutesThisMonthSuffix: MonthlyPulseUnit;
  daysOfMovement: number;
  daysOfMovementSuffix: MonthlyPulseUnit;
  workoutsToDate: number;
  workoutsToDateSuffix: MonthlyPulseUnit;
  workoutsThisMonth: number;
  workoutsThisMonthSuffix: MonthlyPulseUnit;
  caloriesBurntToDate: number;
  caloriesBurntToDateSuffix: MonthlyPulseUnit;
  caloriesBurntThisMonth: number;
  caloriesBurntThisMonthSuffix: MonthlyPulseUnit;
  caloriesBurntFoodEquivalentToDate: number;
  caloriesBurntFoodEquivalentToDateSuffix: MonthlyPulseUnit;
  caloriesBurntFoodEquivalentThisMonth: number;
  caloriesBurntFoodEquivalentThisMonthSuffix: MonthlyPulseUnit;
  caloriesConsumedToDate: number;
  caloriesConsumedToDateSuffix: MonthlyPulseUnit;
  caloriesConsumedBurgerEquivalentToDate: number;
  caloriesConsumedBurgerEquivalentToDateSuffix: MonthlyPulseUnit;
  caloriesConsumedThisMonth: number;
  caloriesConsumedThisMonthSuffix: MonthlyPulseUnit;
  caloriesConsumedBurgerEquivalentThisMonth: number;
  caloriesConsumedBurgerEquivalentThisMonthSuffix: MonthlyPulseUnit;
  workoutsByType: IWorkoutsByType;
  workoutsByTypeTitle: string;
}

export interface INutritionSection {
  popularMealsMedia: IMonthlyPulseMediaItem[];
  popularBlogMedia: IMonthlyPulseMediaItem;
  popularPodcastMedia: IMonthlyPulseMediaItem;
}

export interface IMonthlyPulseData {
  meta: IMonthlyPulseMeta;
  social: ISocialSection;
  wellness: IWellnessSection;
  achievements: IAchievementsSection;
  leaderboards: ILeaderboardsSection;
  exercise: IExerciseSection;
  nutrition: INutritionSection;
}

export interface IBuildMonthlyPulseDataOptions {
  heading: string;
  selectedMonth: string;
  companyName: string | null;
  companyLogoUrl: string | null;
  fitillionLogoUrl: string;
  allTimeMembers: IMemberDetailsData[];
  yearToDateMembers: IMemberDetailsData[];
  thisMonthMembers: IMemberDetailsData[];
  leaderboardConfigs: ILeaderboardConfig[];
  leaderboardInstanceData: ILeaderboardDataViewModel[];
  generatedAt?: Date;
  random?: () => number;
}
